import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { Button, buttonVariants } from "../ui/Button";
import { DesktopDialogOverlay } from "./DesktopDialogOverlay";

/**
 * The desktop confirmation of a destructive action (Figma "Dialog / Confirm",
 * tone Destructive): a title, what is about to go, the note on what follows
 * (a deletion cannot be undone), then Cancel and the action on the right.
 */
export function DesktopConfirmDialog({
  title,
  confirmLabel,
  note,
  disabled,
  confirmDisabled,
  onConfirm,
  onCancel,
  children,
}: {
  title: string;
  confirmLabel: string;
  /** The last line, on what the action leaves behind. */
  note: string;
  /** A write is in flight: neither button acts. */
  disabled: boolean;
  /** Nothing is left to delete (a selection emptied while the dialog was open). */
  confirmDisabled: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <DesktopDialogOverlay
      label={title}
      onClose={onCancel}
      cardClassName="flex w-full max-w-110 flex-col gap-3 rounded-2xl bg-ds-white p-6 shadow-desktop-modal"
    >
      <h2 className="m-0 font-dm-sans text-heading-xs font-semibold text-ds-slate-950">
        {title}
      </h2>
      {children}
      <p className="m-0 font-dm-sans text-desktop-body-s text-ds-slate-700">
        {note}
      </p>
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={onCancel}
        >
          {t("Cancel")}
        </Button>
        <button
          type="button"
          // The dialog's destructive tone is a shade deeper than the DS red.
          className={cn(
            buttonVariants({ variant: "destructive", size: "sm" }),
            "border-ds-cream/32 px-4 [--ds-btn-base:var(--color-ds-strawberry-700)]"
          )}
          disabled={disabled || confirmDisabled}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
      </div>
    </DesktopDialogOverlay>
  );
}
